/** When a session has been left alone too long.
 *
 *  A day's work at the firm is done on shared laptops and on phones that get put
 *  down, so a session that never ends is a session somebody else can walk up to.
 *  After 24 hours with no sign of a person, the session is dropped and the sign-in
 *  screen says why.
 *
 *  **Idle, not absolute.** The clock is reset by a touch, a key or bringing the tab
 *  back to the front - not by the app's own polling, which runs every minute on its
 *  own and would otherwise keep an abandoned tab signed in forever. That is the whole
 *  reason this lives in the browser as well as in the refresh token's lifetime: the
 *  server cannot tell a person from a `refetchInterval`.
 *
 *  The stamp is in `localStorage`, so it is shared by every tab of this app and
 *  survives the browser being closed - a laptop shut on Friday and opened on Monday
 *  asks for a password, which is the point.
 *
 *  Storage throws rather than returning null in a private window and wherever site
 *  data is blocked. Every call here is wrapped, and a missing stamp means "no
 *  evidence of idleness", never "idle": the failure mode of this file must be a
 *  session that lasts, not one that cannot be started.
 */
export const IDLE_MS = 24 * 60 * 60 * 1000

const ACTIVE_KEY = 'tm.lastActive'
const NOTICE_KEY = 'tm.idleSignOut'

/** Something a person did, just now. */
export function markActive(now: number = Date.now()): void {
  try {
    localStorage.setItem(ACTIVE_KEY, String(now))
  } catch {
    // Unstorable is not unusable: the session simply does not idle out in this
    // browser, and the refresh token's own 24 hours still bounds it.
  }
}

/** The last stamp, or null when there is none or it is unreadable. */
export function lastActive(): number | null {
  try {
    const got = localStorage.getItem(ACTIVE_KEY)
    if (!got) return null
    const n = Number(got)
    return Number.isFinite(n) ? n : null
  } catch {
    return null
  }
}

/** Has this session been left alone for a full day?
 *
 *  A stamp in the future - a machine whose clock was wound back, or a laptop
 *  restored from a suspend with the wrong time - is treated as activity rather than
 *  as an enormous negative idle time, and re-stamped by the next thing the person
 *  does.
 */
export function idleTooLong(now: number = Date.now()): boolean {
  const last = lastActive()
  if (last === null) return false
  return now - last >= IDLE_MS
}

export function clearActivity(): void {
  try { localStorage.removeItem(ACTIVE_KEY) } catch { /* nothing to clear */ }
}

/** Leave a note for the sign-in screen. Without it the app simply reappears at the
 *  password box, which reads as a bug - "it logged me out" - rather than as the rule
 *  it is. */
export function noteIdleSignOut(): void {
  try { localStorage.setItem(NOTICE_KEY, '1') } catch { /* no note, no notice */ }
}

/** Read the note and consume it, so it is shown once and not on every later visit. */
export function takeIdleNotice(): boolean {
  try {
    const had = localStorage.getItem(NOTICE_KEY) === '1'
    if (had) localStorage.removeItem(NOTICE_KEY)
    return had
  } catch {
    return false
  }
}
